import type { IKeyedStorageSync, IStorageSync } from '@zajno/common/storage/types';
import { Storages } from '@zajno/common/storage/wrappers';
import type { Nullable } from '@zajno/common/types/misc';
import { StorageUtils } from './utils';
import type { IStorageProvider, StorageBuilderDefaults } from './types';

/** An unkeyed box has nothing to read and nowhere to write. */
const NoopKeyedStorage: IKeyedStorageSync<unknown> = {
  getValue: () => null,
  setValue: () => undefined,
  hasValue: () => false,
  removeValue: () => false,
};

export function createStorage(
  base: IStorageSync<string>,
  utils: StorageUtils,
  defaults: StorageBuilderDefaults = {},
): IStorageProvider {
  return {
    get(key: string) {
      return Storages.toKeyed(
        base,
        utils.prefixStorageKey(key),
      );
    },

    getObject<T extends object>(key: string) {
      return Storages.toKeyed(
        Storages.toJSONConverted<T>(base),
        utils.prefixStorageKey(key),
      );
    },

    getObjectBoxed<T>(key: Nullable<string>, expirationMs: number | undefined = defaults.boxExpirationMs) {
      return !key
        ? NoopKeyedStorage as IKeyedStorageSync<T | null>
        : Storages.toKeyed(this.toObjectBoxedSync<T>(expirationMs), key);
    },

    toObjectBoxedSync<T>(expirationMs: number | undefined = defaults.boxExpirationMs) {
      return utils.toPrefixed(utils.toExpirable<T>(base, expirationMs));
    },

    toStringSync() {
      return utils.toPrefixed(base);
    },

    clear() {
      utils.clearKeys(base);
    },

    createPrefixed(prefix: string, innerDefaults?: StorageBuilderDefaults) {
      return createStorage(
        base,
        utils.addPrefix(() => prefix),
        { ...defaults, ...innerDefaults },
      );
    },
  };
}
